import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Why Register', id: 'why-register' },
    { label: 'Process', id: 'process' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Reviews', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <header data-testid="main-header" className="fixed top-0 left-0 right-0 z-50">
      <div className={`transition-all duration-300 ${isScrolled ? 'bg-[#093336]/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button data-testid="header-logo" onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer">
              <img src="/logo.png" alt="Trademarking Company Name" className="h-10" />
            </button>

            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map(link => (
                <button key={link.id} data-testid={`nav-${link.id}`} onClick={() => scrollToSection(link.id)}
                  className="px-4 py-2 text-sm font-medium text-[#A0B2B2] hover:text-[#A5BF23] transition-colors">
                  {link.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a href="tel:+18001234567" className="flex items-center gap-1.5 text-sm text-[#A0B2B2] hover:text-[#F0F4F4] transition-colors">
                <Phone className="w-3.5 h-3.5" /> +1 (800) 123-4567
              </a>
              <Button data-testid="header-cta-button" onClick={onOpenContactForm}
                className="bg-[#A5BF23] hover:bg-[#8A9F1D] text-[#041B1C] font-semibold px-6 h-10 text-sm rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(165,191,35,0.3)]">
                Free Consultation
              </Button>
            </div>

            <button data-testid="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden text-[#F0F4F4] p-1">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div data-testid="mobile-menu" className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <nav className="flex flex-col gap-1">
                {navLinks.map(link => (
                  <button key={link.id} data-testid={`mobile-nav-${link.id}`} onClick={() => scrollToSection(link.id)}
                    className="text-left px-3 py-2.5 text-sm text-[#A0B2B2] hover:text-[#A5BF23] transition-colors">
                    {link.label}
                  </button>
                ))}
                <div className="pt-3 mt-2 border-t border-white/10">
                  <a href="tel:+18001234567" className="flex items-center gap-2 text-sm text-[#A0B2B2] mb-3 px-3">
                    <Phone className="w-4 h-4" /> +1 (800) 123-4567
                  </a>
                  <Button data-testid="mobile-cta-button" onClick={onOpenContactForm}
                    className="w-full bg-[#A5BF23] hover:bg-[#8A9F1D] text-[#041B1C] font-semibold h-10 rounded-full">
                    Free Consultation
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
